/**
 * Tracking Analytics v1 — Plan d'événements standardisés cross-funnels.
 *
 * Dictionnaire officiel :
 *   funnel_start       — démarrage du funnel (premier render du composant)
 *   step_view          — affichage d'une étape
 *   step_complete      — validation réussie d'une étape
 *   validation_error   — erreur de validation de formulaire
 *   submit_start       — début de la soumission finale
 *   submit_success     — soumission réussie (lead créé)
 *   submit_failure     — soumission échouée (erreur réseau/API)
 *   funnel_abandon     — détecté via sendBeacon on unload
 *   funnel_resume      — reprise d'un funnel précédemment commencé
 *
 * Propriétés communes obligatoires (injectées automatiquement par les helpers) :
 *   funnel_id, audience, segment, intent, source, utm_*, step, step_index,
 *   device, consent_state, tracking_version
 */

import { captureEvent, identify } from './analytics';

/** Version du plan de tracking — à incrémenter si les specs changent. */
const TRACKING_VERSION = 'v1';

export type FunnelId =
  | 'comparateur'
  | 'diagnostic'
  | 'aids_calculator'
  | 'agri_diagnostic'
  | 'renovation'
  | 'contact';

export interface FunnelContext {
  funnelId: FunnelId;
  /** Audience Vike (particulier, pro, entreprise, agri…) */
  audience?: string;
  /** Segment CRM */
  segment?: string;
  /** Intention CEE (cee, comparer, renover, conformite) */
  intent?: string;
  /** Source lead */
  source?: string;
}

/* ─── Session ID ─────────────────────────────────────────── */

let _sessionId: string | undefined;

/**
 * Identifiant de session anonyme (sessionStorage).
 * Source unique pour tracking, consentement et attribution lead.
 */
export function getSessionId(): string {
  if (_sessionId) return _sessionId;
  if (typeof window === 'undefined') return '';

  const key = 'sef_session_id';
  let stored = sessionStorage.getItem(key);
  if (!stored) {
    stored = `sess_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem(key, stored);
  }
  _sessionId = stored;
  return _sessionId;
}

/* ─── UTM helpers ───────────────────────────────────────── */

interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

function readUtm(): UtmParams {
  if (typeof window === 'undefined') return {};
  const p = new URLSearchParams(window.location.search);
  const result: UtmParams = {};
  const keys = ['source', 'medium', 'campaign', 'term', 'content'] as const;
  for (const k of keys) {
    const v = p.get(`utm_${k}`);
    if (v) result[`utm_${k}` as keyof UtmParams] = v;
  }
  return result;
}

/* ─── Device ────────────────────────────────────────────── */

function getDevice(): 'mobile' | 'tablet' | 'desktop' {
  if (typeof window === 'undefined') return 'desktop';
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

/* ─── Consent state ─────────────────────────────────────── */

function getConsentState(): string {
  if (typeof window === 'undefined') return 'unknown';
  try {
    const raw = localStorage.getItem('sef_consent');
    if (!raw) return 'pending';
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (parsed?.hasResponded) return 'responded';
    return 'pending';
  } catch {
    return 'unknown';
  }
}

/* ─── Attribution payload (pour soumission lead) ─────────── */

export interface AttributionPayload {
  sessionId?: string;
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  gclid?: string;
  fbclid?: string;
}

/**
 * Construit le payload d'attribution à inclure dans le body POST /api/leads.
 * Priorité : sessionStorage (1er atterrissage) > URL courante.
 * Cela évite que les UTM de navigation interne écrasent ceux de la landing page.
 */
export function readAttribution(): AttributionPayload {
  if (typeof window === 'undefined') return {};

  const attr: AttributionPayload = {
    sessionId: getSessionId() || undefined,
    referrer: document.referrer || undefined,
  };

  // Lire d'abord le sessionStorage (données du 1er atterrissage)
  try {
    const stored = sessionStorage.getItem('sef_attribution');
    if (stored) {
      const parsed = JSON.parse(stored) as AttributionPayload;
      for (const k of Object.keys(parsed) as (keyof AttributionPayload)[]) {
        if (parsed[k]) attr[k] = parsed[k];
      }
    }
  } catch {
    /* ignore */
  }

  // Compléter par l'URL courante uniquement si valeur absente du sessionStorage
  const p = new URLSearchParams(window.location.search);
  const urlParams: AttributionPayload = {
    utmSource: p.get('utm_source') || undefined,
    utmMedium: p.get('utm_medium') || undefined,
    utmCampaign: p.get('utm_campaign') || undefined,
    utmTerm: p.get('utm_term') || undefined,
    utmContent: p.get('utm_content') || undefined,
    gclid: p.get('gclid') || undefined,
    fbclid: p.get('fbclid') || undefined,
  };
  for (const k of Object.keys(urlParams) as (keyof AttributionPayload)[]) {
    if (!attr[k] && urlParams[k]) attr[k] = urlParams[k];
  }

  return attr;
}

/**
 * Persiste les paramètres d'attribution au premier atterrissage (landing page).
 * À appeler dans le Layout ou la première page visitée.
 */
export function persistAttribution(): void {
  if (typeof window === 'undefined') return;
  try {
    const existing = sessionStorage.getItem('sef_attribution');
    if (existing) return; // Ne pas écraser si déjà présent dans la session
    const attr = readAttribution();
    const hasData = Object.values(attr).some((v) => v && v !== attr.sessionId);
    if (hasData) {
      sessionStorage.setItem('sef_attribution', JSON.stringify(attr));
    }
  } catch {
    /* ignore */
  }
}

/* ─── Common properties builder ─────────────────────────── */

interface CommonProps {
  funnel_id: FunnelId;
  session_id: string;
  device: 'mobile' | 'tablet' | 'desktop';
  consent_state: string;
  tracking_version: string;
  audience?: string;
  segment?: string;
  intent?: string;
  source?: string;
}

function buildCommon(ctx: FunnelContext): CommonProps & UtmParams {
  return {
    funnel_id: ctx.funnelId,
    session_id: getSessionId(),
    device: getDevice(),
    consent_state: getConsentState(),
    tracking_version: TRACKING_VERSION,
    ...(ctx.audience ? { audience: ctx.audience } : {}),
    ...(ctx.segment ? { segment: ctx.segment } : {}),
    ...(ctx.intent ? { intent: ctx.intent } : {}),
    ...(ctx.source ? { source: ctx.source } : {}),
    ...readUtm(),
  };
}

/* ─── Public tracking helpers ───────────────────────────── */

export function trackFunnelStart(ctx: FunnelContext, extra?: Record<string, unknown>): void {
  captureEvent('funnel_start', { ...buildCommon(ctx), ...extra });
}

export function trackStepView(
  ctx: FunnelContext,
  step: string,
  stepIndex: number,
  extra?: Record<string, unknown>,
): void {
  captureEvent('step_view', {
    ...buildCommon(ctx),
    step,
    step_index: stepIndex,
    ...extra,
  });
}

export function trackStepComplete(
  ctx: FunnelContext,
  step: string,
  stepIndex: number,
  extra?: Record<string, unknown>,
): void {
  captureEvent('step_complete', {
    ...buildCommon(ctx),
    step,
    step_index: stepIndex,
    ...extra,
  });
}

export function trackValidationError(
  ctx: FunnelContext,
  step: string,
  fields: string[],
  extra?: Record<string, unknown>,
): void {
  captureEvent('validation_error', {
    ...buildCommon(ctx),
    step,
    fields,
    ...extra,
  });
}

export function trackSubmitStart(ctx: FunnelContext, extra?: Record<string, unknown>): void {
  captureEvent('submit_start', { ...buildCommon(ctx), ...extra });
}

/**
 * À appeler immédiatement après une soumission réussie.
 * - Envoie l'event `submit_success` avec lead_id pour le tracking funnel.
 * - Appelle PostHog `identify` pour relier la session anonyme au lead (stitch CRM).
 *   `email` est le distinctId stable ; les props supplémentaires enrichissent le profil.
 */
export function trackSubmitSuccess(
  ctx: FunnelContext,
  leadId: string,
  identifyProps?: { email?: string; segment?: string; source?: string; [k: string]: unknown },
  extra?: Record<string, unknown>,
): void {
  const common = buildCommon(ctx);
  captureEvent('submit_success', {
    ...common,
    lead_id: leadId,
    ...extra,
  });

  // Stitch session anonyme → lead identifié dans PostHog
  if (identifyProps?.email) {
    identify(identifyProps.email, {
      lead_id: leadId,
      segment: identifyProps.segment ?? ctx.segment,
      source: identifyProps.source ?? ctx.source,
      funnel_id: ctx.funnelId,
      session_id: common.session_id,
      ...readUtm(),
      ...identifyProps,
    });
  }
}

export function trackSubmitFailure(
  ctx: FunnelContext,
  errorType: 'network' | 'api' | 'validation' | 'unknown',
  extra?: Record<string, unknown>,
): void {
  captureEvent('submit_failure', {
    ...buildCommon(ctx),
    error_type: errorType,
    ...extra,
  });
}

/**
 * À appeler via sendBeacon dans beforeunload / pagehide pour capter les abandons.
 *
 * Stratégie double :
 *  1. sendBeacon → endpoint léger /api/analytics/abandon — fonctionne même sans
 *     consentement analytics et résiste aux fermetures rapides de page.
 *  2. captureEvent (PostHog) — enrichit si PostHog est actif et consentement accordé.
 */
export function trackFunnelAbandon(ctx: FunnelContext, step: string, stepIndex: number): void {
  const common = buildCommon(ctx);
  const payload = {
    funnel_id: ctx.funnelId,
    step,
    step_index: stepIndex,
    session_id: common.session_id,
    device: common.device,
  };

  // sendBeacon — résiste aux fermetures de page, pas de blocage consentement
  if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
    navigator.sendBeacon(
      '/api/analytics/abandon',
      new Blob([JSON.stringify(payload)], { type: 'application/json' }),
    );
  }

  // PostHog en complément (si consentement accordé)
  captureEvent('funnel_abandon', { ...common, step, step_index: stepIndex });
}

export function trackFunnelResume(
  ctx: FunnelContext,
  step: string,
  stepIndex: number,
  extra?: Record<string, unknown>,
): void {
  captureEvent('funnel_resume', {
    ...buildCommon(ctx),
    step,
    step_index: stepIndex,
    ...extra,
  });
}
